import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ActionableNotification, Button, InlineNotification, Modal, NumberInput, Pagination, Select, SelectItem,
  Tab, TabList, TabPanel, TabPanels, Tabs, Tag, TextArea,
} from '@carbon/react';
import type { ColumnDef } from '@tanstack/react-table';
import { formatVnDateTime } from '@ruby-cms/shared';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { DataTable } from '../components/DataTable';
import { BlockSkeleton, ErrorState, InlineError, PageHeader } from '../components/states';
import { When } from '../components/format';
import { FilterBar, useUrlFilters, type FilterField } from '../components/Filters';
import { HE_A_ERRORS } from '../components/labels';
import type { Command } from './Commands';

// Chat moderation (P1-D11): the chat log of every game, delete a line from every open chat, mute a
// player, a system line into a game's chat, and the banned-word list. He A / he B apply each command
// at once; this page only reads the log and sends commands.

type Room = { id: string; name: string; open: boolean };
type Rooms = { rooms: Room[]; heA: boolean; heB: boolean };
type Line = {
  id: number; at: number; game: string; gameName: string; room: string; userId: number | null; name: string | null;
  text: string; original: string | null; kind: 'nguoi' | 'he_thong'; deletedAt: number | null; deleteReason: string | null;
  mutedUntil: number | null;
};
type Page = { items: Line[]; total: number; page: number; pageSize: number };
type Banned = { doDaiToiDa: number; giayGiuaHaiTin: number; soTinLichSu: number; luuNgay: number; tuCam: string[]; tuCamKhongDau: string[] };
type BannedData = { version: number; custom: boolean; doc: Banned | null };

// mute presets in minutes; 10 years is the longest he A takes ("until lifted")
const MUTE: [number, string][] = [[15, '15 phút'], [60, '1 giờ'], [24 * 60, '24 giờ'], [7 * 24 * 60, '7 ngày'], [30 * 24 * 60, '30 ngày'], [10 * 365 * 24 * 60, 'Đến khi bỏ cấm']];

function commandNote(c: Command | null, ok: string) {
  if (!c) return null;
  const done = c.status === 'SUCCEEDED';
  return { kind: done ? 'success' as const : 'warning' as const, title: done ? ok : 'Lệnh chưa xong',
    subtitle: done ? '' : (c.lastError && HE_A_ERRORS[c.lastError]) || c.lastError || c.status };
}

function DeleteModal({ line, onClose }: { line: Line; onClose: (c: Command | null) => void }) {
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () => api.post<{ command: Command }>(`/api/admin/chat/${line.id}/delete`, { reason }),
    onSuccess: (r) => onClose(r.command),
  });
  return (
    <Modal open danger modalHeading="Xóa tin chat" primaryButtonText="Xóa khỏi mọi khung chat" secondaryButtonText="Hủy"
      primaryButtonDisabled={m.isPending} onRequestClose={() => onClose(null)} onRequestSubmit={() => m.mutate()}>
      <div className="stack">
        <p><strong>{line.name ?? 'Hệ thống'}</strong> ({line.gameName}, <When v={line.at} />): {line.text}</p>
        <p>Người đang mở Tài Xỉu hoặc bàn chơi thấy dòng này biến mất ngay; các mini game khác bỏ dòng khỏi lịch sử cho người vào sau.</p>
        <TextArea id="chat-del-reason" labelText="Lý do" rows={2} maxCount={200} enableCounter value={reason} onChange={(e) => setReason(e.target.value)} />
        <InlineError error={m.error} />
      </div>
    </Modal>
  );
}

function MuteModal({ line, onClose }: { line: Line; onClose: (c: Command | null) => void }) {
  const [minutes, setMinutes] = useState(line.mutedUntil ? 0 : 60);
  const m = useMutation({
    mutationFn: () => api.post<{ command: Command }>('/api/admin/chat/mute', { userId: line.userId, minutes }),
    onSuccess: (r) => onClose(r.command),
  });
  return (
    <Modal open modalHeading={'Cấm chat ' + (line.name ?? '#' + line.userId)} primaryButtonText={minutes ? 'Cấm chat' : 'Bỏ cấm chat'} secondaryButtonText="Hủy"
      primaryButtonDisabled={m.isPending} onRequestClose={() => onClose(null)} onRequestSubmit={() => m.mutate()}>
      <div className="stack">
        {line.mutedUntil ? <p>Đang bị cấm chat đến <When v={line.mutedUntil} />.</p> : null}
        <p>Người bị cấm gửi tin ở mọi khung chat (Tài Xỉu, mini game, bàn chơi) chỉ thấy dòng báo cấm; không ai khác thấy tin đó.</p>
        <Select id="chat-mute-len" labelText="Thời gian" value={String(minutes)} onChange={(e) => setMinutes(Number(e.target.value))}>
          {MUTE.map(([v, t]) => <SelectItem key={v} value={String(v)} text={t} />)}
          {line.mutedUntil ? <SelectItem value="0" text="Bỏ cấm chat" /> : null}
        </Select>
        <InlineError error={m.error} />
      </div>
    </Modal>
  );
}

function LogTab({ rooms }: { rooms: Room[] }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const fields: FilterField[] = [
    { key: 'player', label: 'Người chơi (tên hoặc mã)', kind: 'text' },
    { key: 'q', label: 'Nội dung có chữ', kind: 'text' },
    { key: 'game', label: 'Game', kind: 'select', options: [['', 'Mọi game'], ...rooms.map((r) => [r.id, r.name] as [string, string])] },
    { key: 'status', label: 'Trạng thái', kind: 'select', options: [['', 'Tất cả'], ['live', 'Đang hiện'], ['deleted', 'Đã xóa']] },
    { key: 'kind', label: 'Loại tin', kind: 'select', options: [['', 'Tất cả'], ['nguoi', 'Người chơi'], ['he_thong', 'Tin hệ thống']] },
    { key: 'from', label: 'Từ ngày', kind: 'date' },
    { key: 'to', label: 'Đến ngày', kind: 'date' },
  ];
  const f = useUrlFilters(fields.map((x) => x.key));
  const q = useQuery({
    queryKey: ['chat-log', f.query, f.page, f.pageSize],
    queryFn: () => api.get<Page>('/api/admin/chat', { ...f.query, page: f.page, pageSize: f.pageSize }),
    placeholderData: keepPreviousData,
    refetchInterval: 10000,
  });
  const [del, setDel] = useState<Line | null>(null);
  const [mute, setMute] = useState<Line | null>(null);
  const [note, setNote] = useState<ReturnType<typeof commandNote>>(null);
  const closed = (ok: string) => (c: Command | null) => {
    setDel(null); setMute(null);
    if (c) { setNote(commandNote(c, ok)); qc.invalidateQueries({ queryKey: ['chat-log'] }); }
  };
  const moderate = can('chat.moderate');
  const columns: ColumnDef<Line, any>[] = [
    { header: 'Lúc', accessorKey: 'at', cell: (c) => <When v={c.getValue()} /> },
    { header: 'Game', accessorKey: 'gameName' },
    { header: 'Người gửi', id: 'who', accessorFn: (r) => r.name ?? '', cell: (c) => {
      const r = c.row.original;
      if (r.kind === 'he_thong' || r.userId == null) return <Tag size="sm" type="purple">Hệ thống</Tag>;
      return <span className="row">
        <Link className="cell-link" to={`/users/${r.userId}`}>{r.name ?? '#' + r.userId}</Link>
        {r.mutedUntil ? <Tag size="sm" type="red" title={'đến ' + formatVnDateTime(r.mutedUntil)}>Đang cấm chat</Tag> : null}
      </span>;
    } },
    { header: 'Nội dung', accessorKey: 'text', enableSorting: false, cell: (c) => {
      const r = c.row.original;
      return <div className={r.deletedAt ? 'chat-line chat-line--deleted' : 'chat-line'}>
        <span>{r.text}</span>
        {r.original ? <span className="muted"> Gõ: {r.original}</span> : null}
      </div>;
    } },
    { header: 'Trạng thái', id: 'st', accessorFn: (r) => r.deletedAt ?? 0, cell: (c) => {
      const r = c.row.original;
      return r.deletedAt ? <span><Tag size="sm" type="gray">Đã xóa</Tag>{r.deleteReason ? <span className="muted"> {r.deleteReason}</span> : null}</span>
        : <Tag size="sm" type="green">Đang hiện</Tag>;
    } },
    ...(moderate ? [{ header: '', id: 'act', enableSorting: false, cell: (c: { row: { original: Line } }) => {
      const r = c.row.original;
      return <span className="row">
        {!r.deletedAt ? <Button size="sm" kind="ghost" onClick={() => setDel(r)}>Xóa</Button> : null}
        {r.userId != null ? <Button size="sm" kind="ghost" onClick={() => setMute(r)}>{r.mutedUntil ? 'Bỏ / đổi cấm' : 'Cấm chat'}</Button> : null}
      </span>;
    } } as ColumnDef<Line, any>] : []),
  ];
  return (
    <div className="stack">
      {note ? <InlineNotification kind={note.kind} lowContrast title={note.title} subtitle={note.subtitle} onClose={() => setNote(null)} /> : null}
      <FilterBar id="chat" fields={fields} filters={f} />
      <DataTable columns={columns} data={q.data?.items} loading={q.isLoading || q.isPlaceholderData} error={q.error}
        onRetry={() => q.refetch()} rowKey={(r) => String(r.id)}
        empty={{ title: f.active ? 'Không có tin khớp bộ lọc' : 'Chưa có tin chat nào', body: f.active ? 'Nới bộ lọc để xem thêm.' : 'Tin người chơi gõ trong Tài Xỉu, mini game và bàn chơi hiện ở đây.' }} />
      {q.data && q.data.total > 0 ? (
        <Pagination page={f.page} pageSize={f.pageSize} pageSizes={[25, 50, 100, 200]} totalItems={q.data.total}
          itemsPerPageText="Mỗi trang" pageRangeText={(_c, t) => `trên ${t} trang`}
          itemRangeText={(min, max, total) => `${min} đến ${max} trong ${total.toLocaleString('vi-VN')} tin`}
          onChange={({ page: p, pageSize: s }) => f.setPage(p, s)} />
      ) : null}
      {del ? <DeleteModal line={del} onClose={closed('Đã xóa tin khỏi mọi khung chat')} /> : null}
      {mute ? <MuteModal line={mute} onClose={closed('Đã cập nhật cấm chat')} /> : null}
    </div>
  );
}

function SendTab({ data }: { data: Rooms }) {
  const { can } = useAuth();
  const open = data.rooms.filter((r) => r.open);
  const [game, setGame] = useState(open[0]?.id ?? '*');
  const [text, setText] = useState('');
  const [note, setNote] = useState<ReturnType<typeof commandNote>>(null);
  const m = useMutation({
    mutationFn: () => api.post<{ command: Command }>('/api/admin/chat/send', { game, text }),
    onSuccess: (r) => { setNote(commandNote(r.command, 'Đã gửi tin hệ thống')); if (r.command.status === 'SUCCEEDED') setText(''); },
  });
  const disabled = !can('chat.moderate');
  return (
    <div className="stack chat-send">
      <p>Dòng chữ hiện trong khung chat của game như tin hệ thống, người đang mở thấy ngay, người vào sau thấy trong lịch sử. Mỗi lần bấm gửi một lần.</p>
      {!data.heB ? <InlineNotification kind="warning" lowContrast hideCloseButton title="Hệ B không trả lời" subtitle="Chỉ gửi được vào bàn chơi của hệ A lúc này." /> : null}
      <Select id="chat-send-game" labelText="Khung chat" value={game} disabled={disabled} onChange={(e) => setGame(e.target.value)}>
        <SelectItem value="*" text="Mọi khung chat đang mở" />
        {open.map((r) => <SelectItem key={r.id} value={r.id} text={r.name} />)}
      </Select>
      <TextArea id="chat-send-text" labelText="Nội dung" rows={3} maxCount={200} enableCounter value={text} disabled={disabled}
        onChange={(e) => setText(e.target.value)} placeholder="Ví dụ: Hệ thống bảo trì lúc 23:00, vui lòng không đặt cược sau 22:55" />
      <div><Button disabled={disabled || m.isPending || !text.trim()} onClick={() => m.mutate()}>Gửi vào khung chat</Button></div>
      <InlineError error={m.error} />
      {note ? <InlineNotification kind={note.kind} lowContrast title={note.title} subtitle={note.subtitle} onClose={() => setNote(null)} /> : null}
    </div>
  );
}

const lines = (s: string) => s.split('\n').map((x) => x.trim()).filter(Boolean);
const isStale = (e: unknown) => e instanceof ApiError && e.status === 409 && e.code === 'STALE';

function BannedTab() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['chat-banned'], queryFn: () => api.get<BannedData>('/api/admin/chat/banned') });
  const [d, setD] = useState<(Omit<Banned, 'tuCam' | 'tuCamKhongDau'> & { tuCam: string; tuCamKhongDau: string }) | null>(null);
  const [base, setBase] = useState<number | null>(null);
  const [note, setNote] = useState<ReturnType<typeof commandNote>>(null);
  // After a save or a discard the editor refills only from a fetch newer than that moment: the cached
  // (previous) document must not come back as the draft with its old version.
  const [after, setAfter] = useState(0);
  useEffect(() => {
    if (q.data?.doc && d === null && q.dataUpdatedAt > after) {
      const x = q.data.doc;
      setD({ ...x, tuCam: x.tuCam.join('\n'), tuCamKhongDau: x.tuCamKhongDau.join('\n') });
      setBase(q.data.version);
    }
  }, [q.data, q.dataUpdatedAt, d, after]);
  const reload = () => { setAfter(q.dataUpdatedAt); setD(null); void qc.invalidateQueries({ queryKey: ['chat-banned'] }); };
  const m = useMutation({
    mutationFn: () => api.put<{ command: Command }>('/api/admin/chat/banned', {
      doc: { ...d!, tuCam: lines(d!.tuCam), tuCamKhongDau: lines(d!.tuCamKhongDau) }, expect: base,
    }),
    onSuccess: (r) => { setNote(commandNote(r.command, 'Hệ A và hệ B che từ mới từ tin kế tiếp')); reload(); },
  });
  // a failed refetch after a save or a discard shows the error with a retry, not an endless skeleton
  if (q.isError && !d) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (q.isLoading || (q.data?.doc && !d)) return <BlockSkeleton lines={8} />;
  if (q.error || !q.data?.doc || !d) return <ErrorState error={q.error ?? new Error('Hệ A chưa trả danh sách từ cấm')} onRetry={() => q.refetch()} />;
  const editable = can('chat.config');
  const num = (k: 'doDaiToiDa' | 'giayGiuaHaiTin' | 'soTinLichSu' | 'luuNgay', label: string, min: number, max: number, help: string) => (
    <NumberInput id={'chat-' + k} label={label} helperText={help} min={min} max={max} step={1} value={d[k]} disabled={!editable} allowEmpty={false}
      onChange={(_e, { value }) => setD({ ...d, [k]: Number(value) })} />
  );
  return (
    <div className="stack">
      <p className="row health">
        {q.data.custom ? <span>Phiên bản <span className="num">{q.data.version}</span> đặt từ CMS</span> : <span className="muted">Đang dùng danh sách mặc định của máy chủ (chưa lưu từ CMS)</span>}
      </p>
      {note ? <InlineNotification kind={note.kind} lowContrast title={note.title} subtitle={note.subtitle} onClose={() => setNote(null)} /> : null}
      {isStale(m.error) ? (
        <ActionableNotification kind="warning" inline lowContrast hideCloseButton title="Danh sách vừa được người khác lưu"
          subtitle="Chưa lưu gì. Tải bản mới rồi sửa lại." actionButtonLabel="Bỏ bản nháp và tải lại"
          onActionButtonClick={() => { m.reset(); reload(); }} />
      ) : <InlineError error={m.error} />}
      <div className="chat-cols">
        <TextArea id="chat-tu-cam" labelText={`Từ cấm, khớp đúng dấu (${lines(d.tuCam).length} từ)`} rows={14} disabled={!editable}
          helperText="Mỗi dòng một từ hoặc cụm từ. Khớp cả từ, không phân biệt hoa thường; ký tự trùng bị thay bằng *."
          value={d.tuCam} onChange={(e) => setD({ ...d, tuCam: e.target.value })} />
        <TextArea id="chat-tu-cam-kd" labelText={`Từ cấm viết tắt, khớp cả khi bỏ dấu (${lines(d.tuCamKhongDau).length} từ)`} rows={14} disabled={!editable}
          helperText="Ví dụ vcl, dm: người chơi gõ có dấu hay không dấu đều bị che."
          value={d.tuCamKhongDau} onChange={(e) => setD({ ...d, tuCamKhongDau: e.target.value })} />
      </div>
      <div className="control-grid">
        {num('doDaiToiDa', 'Độ dài tối đa một tin', 10, 500, 'Ký tự; dài hơn bị cắt')}
        {num('giayGiuaHaiTin', 'Giây giữa hai tin', 0, 600, 'Của cùng một người')}
        {num('soTinLichSu', 'Số tin lịch sử ở bàn chơi', 1, 50, 'Người vào bàn thấy bao nhiêu tin cũ')}
        {num('luuNgay', 'Giữ nhật ký chat (ngày)', 1, 3650, 'Tin cũ hơn bị xóa khỏi nhật ký')}
      </div>
      {editable ? <div><Button disabled={m.isPending || lines(d.tuCam).length + lines(d.tuCamKhongDau).length === 0} onClick={() => m.mutate()}>Lưu danh sách</Button></div> : null}
    </div>
  );
}

export function ChatPage() {
  const { can } = useAuth();
  const [tab, setTab] = useState(0);
  const rooms = useQuery({ queryKey: ['chat-rooms'], queryFn: () => api.get<Rooms>('/api/admin/chat/rooms'), refetchInterval: 30000 });
  return (
    <div className="page">
      <PageHeader title="Chat trong game"
        lead="Mọi tin người chơi gõ trong Tài Xỉu, mini game và bàn chơi. Xóa tin, cấm chat và tin hệ thống là lệnh gửi hệ A, áp dụng ngay cho người đang chơi." />
      {rooms.isLoading ? <BlockSkeleton lines={6} /> : rooms.error || !rooms.data ? <ErrorState error={rooms.error} onRetry={() => rooms.refetch()} /> : (
        <Tabs selectedIndex={tab} onChange={({ selectedIndex }) => setTab(selectedIndex)}>
          <TabList aria-label="Chat">
            <Tab>Nhật ký chat</Tab>
            <Tab disabled={!can('chat.moderate')}>Gửi tin hệ thống</Tab>
            <Tab>Từ cấm</Tab>
          </TabList>
          <TabPanels>
            <TabPanel><LogTab rooms={rooms.data.rooms} /></TabPanel>
            <TabPanel>{tab === 1 ? <SendTab data={rooms.data} /> : null}</TabPanel>
            <TabPanel>{tab === 2 ? <BannedTab /> : null}</TabPanel>
          </TabPanels>
        </Tabs>
      )}
    </div>
  );
}
